<template>
  <section class="min-h-[87vh] flex bg-[#f8fafc] w-full overflow-x-hidden relative z-0">
    <div class="w-full flex flex-col relative z-0">
      <!-- Container Padding -->
      <div class="flex-1 flex flex-col items-center py-4 px-4 md:py-6 md:px-8">
        <div class="w-full max-w-[1400px] mx-auto">

          <!-- Header Section (Unchanged) -->
          <div v-if="!isStepSelected" class="mb-4 w-full" :class="viewMode === 'simple' ? 'max-w-6xl mx-auto' : ''">
             <!-- ... (Header content unchanged) ... -->
             <!-- Keep your existing header code here -->
             <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-3">
              <div class="flex-1 text-left">
                <h3 class="text-[28px] leading-tight font-semibold text-[#2c3e50]">
                  Welcome {{ orgData.name }} 👋
                </h3>
              </div>
              <!-- View Mode Toggler -->
              <div class="flex items-center gap-3 shrink-0 ml-3">
                <div
                  class="w-12 h-12 rounded-lg cursor-pointer shadow-md 
                         transition-all duration-200 
                         flex items-center justify-center 
                         bg-primary hover:bg-[#e0ab00]"
                  @click="viewMode = viewMode === 'detailed' ? 'simple' : 'detailed'"
                >
                  <vx-tooltip
                    :text="viewMode === 'detailed' ? 'Switch to Simple View' : 'Switch to Detailed View'"
                    position="bottom"
                    class="flex items-center"
                  >
                    <feather-icon
                      :icon="viewMode === 'detailed' ? 'GridIcon' : 'ListIcon'"
                      svgClasses="w-6 h-6"
                      style="color: #ffffff"
                    />
                  </vx-tooltip>
                </div>
              </div>
            </div>

            <p v-if="viewMode === 'simple'" class="text-[#666] text-lg w-full text-left">
              Let's set up your talent pool to kick off your KVDR journey and power your talent strategy!
            </p>
            <p v-else class="text-[#666] text-lg w-full text-left">
              Follow these steps to align talent to what truly drives business outcomes and build a data-backed talent
              ecosystem.
            </p>
          </div>

          <!-- Step Selection List (SIMPLE VIEW - Unchanged) -->
          <div v-if="!isStepSelected && viewMode === 'simple'"
            class="space-y-4 bg-white rounded-2xl p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] border border-gray-100 animate-fade-in-up max-w-6xl mx-auto">
             <!-- ... (Simple view content unchanged) ... -->
              <div v-for="step in steps" :key="step.id" @click="selectStep(step.id)"
              class="step-item p-4 rounded-xl transition-all duration-200 hover:shadow-lg bg-gray-50/30 hover:bg-white"
              :class="{
                'cursor-pointer transform hover:-translate-y-1': true,
                'opacity-75 hover:opacity-90': step.id > currentStep && currentStep <= 4,
                'steps-container': step.id === currentStep,
              }">
              <div class="flex items-center gap-6">
                <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg shadow-sm transition-all duration-300"
                  :class="{
                    'bg-green text-white': step.id < currentStep || step.completed,
                    'bg-[#f5bb00] text-black': step.id === currentStep && !step.completed,
                    'bg-gray-100 text-gray-400': step.id > currentStep
                  }">
                  <template v-if="step.id < currentStep || step.completed">
                    <CheckIcon size="20" />
                  </template>
                  <template v-else>
                    {{ step.id }}
                  </template>
                </div>

                <div class="flex-1 text-left">
                  <h3 class="text-lg font-semibold mb-1"
                    :class="{ 'text-[#2c3e50]': step.id <= currentStep, 'text-gray-400': step.id > currentStep }">
                    {{ step.title }}
                  </h3>
                  <p class="text-[#666] text-sm leading-relaxed">{{ step.description }}</p>
                </div>
                <div :class="{ 'text-[#f5bb00]': step.id < currentStep, 'text-gray-300': step.id > currentStep }">
                  <feather-icon icon="ChevronRightIcon" class="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>

          <!-- Step Selection Grid (DETAILED VIEW - Unchanged) -->
          <div v-if="!isStepSelected && viewMode === 'detailed'" class="animate-fade-in-up pb-2">
            <!-- ... (Detailed grid content unchanged) ... -->
            <!-- Use your existing detailed grid code here -->
             <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                 <!-- Insert Card 1, 2, 3, 4 from your original code here -->
                 <!-- (Omitting for brevity as requested to update required parts, but assuming they exist) -->
                  <!-- CARD 1 (Step 1) -->
    <div
      class="bg-white rounded-xl border border-gray-200 p-5 flex flex-col h-full hover:border-[#f5bb00] hover:shadow-lg transition-all duration-300 relative group shadow-sm text-left"
      :class="{ 'ring-2 ring-[#f5bb00] border-[#f5bb00]': currentStep === 1 }">
      
      <div class="flex justify-between items-start mb-3">
        <div class="text-[#2c3e50] group-hover:text-[#f5bb00] transition-colors">
          <feather-icon icon="LayoutIcon" class="w-8 h-8" />
        </div>
        <span class="text-xs font-bold text-gray-300 select-none">01</span>
      </div>

      <div class="min-h-[48px] flex items-start mb-1">
        <h3 class="text-lg font-bold text-[#2c3e50] text-left leading-snug">
          Define KVDRs-Critical Roles
        </h3>
      </div>

      <div class="min-h-[24px] mb-4">
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide text-left">
          ALIGN TALENT TO WHAT DRIVES BUSINESS
        </p>
      </div>

      <div class="mb-4">
        <div class="min-h-[32px] flex items-start mb-2">
          <p class="text-[13.5px] text-[#2c3e50] font-bold text-left leading-snug">
            Map your organization by identifying:
          </p>
        </div>
        <ul class="space-y-2.5 text-left">
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
            <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Core functions & departments</span>
          </li>
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
            <div class="shrink-0 mt-0.5 text-[#f5bb00]">
              <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Key Value Delivery Roles (roles that materially impact strategy & growth)</span>
          </li>
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
             <div class="shrink-0 mt-0.5 text-[#f5bb00]">
                <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Reporting lines and role criticality</span>
          </li>
        </ul>
      </div>

      <div class="pt-3 mt-auto border-t border-gray-100 text-left space-y-3">
        <p class="text-[11px] text-gray-500 leading-relaxed">
          <span class="font-bold text-[#2c3e50] block mb-0.5">Outcome:</span>
          A business-aligned organizational blueprint with a look of an org chart.
        </p>

        <!-- Button Logic for Step 1 -->
        <button
          @click="selectStep(1)"
          class="w-full py-3 px-4 rounded-lg text-sm font-bold transition-all duration-200 border-2 flex items-center justify-center gap-2"
          :class="{
            'btn-grid-completed': steps[0].completed || 1 < currentStep,
            'btn-grid-start': 1 === currentStep && !steps[0].completed,
            'bg-white border-gray-200 text-gray-300 cursor-not-allowed hover:bg-gray-50': 1 > currentStep
          }"
          :disabled="1 > currentStep"
        >
          <span v-if="steps[0].completed || 1 < currentStep" class="flex items-center gap-2">
            Completed
            <div class="bg-white rounded-full flex items-center justify-center w-5 h-5">
              <feather-icon icon="CheckIcon" class="w-3 h-3 text-green" />
            </div>
          </span>
          <span v-else-if="1 === currentStep" class="flex items-center gap-2">
            Start Now <feather-icon icon="ArrowRightIcon" class="w-4 h-4" />
          </span>
          <span v-else class="flex items-center gap-2">
            Pending <feather-icon icon="LockIcon" class="w-3 h-3" />
          </span>
        </button>
      </div>
    </div>

    <!-- CARD 2 (Step 2) -->
    <div
      class="bg-white rounded-xl border border-gray-200 p-5 flex flex-col h-full hover:border-[#f5bb00] hover:shadow-lg transition-all duration-300 relative group shadow-sm text-left"
      :class="{ 'ring-2 ring-[#f5bb00] border-[#f5bb00]': currentStep === 2 }">
      
      <div class="flex justify-between items-start mb-3">
        <div class="text-[#2c3e50] group-hover:text-[#f5bb00] transition-colors">
          <feather-icon icon="UsersIcon" class="w-8 h-8" />
        </div>
        <span class="text-xs font-bold text-gray-300 select-none">02</span>
      </div>

      <div class="min-h-[48px] flex items-start mb-1">
        <h3 class="text-lg font-bold text-[#2c3e50] text-left leading-snug">
          Build Your Talent Ecosystem
        </h3>
      </div>

      <div class="min-h-[24px] mb-4">
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide text-left">
          SINGLE SOURCE OF TRUTH FOR YOUR PEOPLE DATA
        </p>
      </div>

      <div class="mb-4">
        <div class="min-h-[32px] flex items-start mb-2">
          <p class="text-[13.5px] text-[#2c3e50] font-bold text-left leading-snug">
            Bring your workforce into GetBoarded by:
          </p>
        </div>
        <ul class="space-y-2.5 text-left">
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
            <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Uploading CVs, Excel, or role profiles</span>
          </li>
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
             <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Connecting your HRIS (if available)</span>
          </li>
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
             <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Enrichment with skills, abilities & aspirations</span>
          </li>
        </ul>
      </div>

      <div class="pt-3 mt-auto border-t border-gray-100 text-left space-y-3">
        <p class="text-[11px] text-gray-500 leading-relaxed">
          <span class="font-bold text-[#2c3e50] block mb-0.5">Outcome:</span>
          A live, searchable talent pool ready for analysis; internal & external.
        </p>

        <!-- Button Logic for Step 2 -->
        <button
          @click="selectStep(2)"
          class="w-full py-3 px-4 rounded-lg text-sm font-bold transition-all duration-200 border-2 flex items-center justify-center gap-2"
          :class="{
            'btn-grid-completed': steps[1].completed || 2 < currentStep,
            'btn-grid-start': 2 === currentStep && !steps[1].completed,
            'bg-white border-gray-200 text-gray-300 cursor-not-allowed hover:bg-gray-50': 2 > currentStep
          }"
          :disabled="2 > currentStep"
        >
          <span v-if="steps[1].completed || 2 < currentStep" class="flex items-center gap-2">
            Completed
            <div class="bg-white rounded-full flex items-center justify-center w-5 h-5">
              <feather-icon icon="CheckIcon" class="w-3 h-3 text-green" />
            </div>
          </span>
          <span v-else-if="2 === currentStep" class="flex items-center gap-2">
            Start Now <feather-icon icon="ArrowRightIcon" class="w-4 h-4" />
          </span>
          <span v-else class="flex items-center gap-2">
            Pending <feather-icon icon="LockIcon" class="w-3 h-3" />
          </span>
        </button>
      </div>
    </div>

    <!-- CARD 3 (Step 3) -->
    <div
      class="bg-white rounded-xl border border-gray-200 p-5 flex flex-col h-full hover:border-[#f5bb00] hover:shadow-lg transition-all duration-300 relative group shadow-sm text-left"
      :class="{ 'ring-2 ring-[#f5bb00] border-[#f5bb00]': currentStep === 3 }">
      
      <div class="flex justify-between items-start mb-3">
        <div class="text-[#2c3e50] group-hover:text-[#f5bb00] transition-colors">
          <feather-icon icon="TrendingUpIcon" class="w-8 h-8" />
        </div>
        <span class="text-xs font-bold text-gray-300 select-none">03</span>
      </div>

      <div class="min-h-[48px] flex items-start mb-1">
        <h3 class="text-lg font-bold text-[#2c3e50] text-left leading-snug">
          Evaluate Career Trajectories
        </h3>
      </div>

      <div class="min-h-[24px] mb-4">
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide text-left">
          BEYOND PERFORMANCE & POTENTIAL STATS
        </p>
      </div>

      <div class="mb-4">
        <div class="min-h-[32px] flex items-start mb-2">
          <p class="text-[13.5px] text-[#2c3e50] font-bold text-left leading-snug">
            Configure how talent is assessed by:
          </p>
        </div>
        <ul class="space-y-2.5 text-left">
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
             <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Using GetBoarded's trajectory models (Fitment, Performance, Succession)</span>
          </li>
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
             <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Customizing parameters aligned to your business context</span>
          </li>
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
             <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Weighing signals that matter now and in the future</span>
          </li>
        </ul>
      </div>

      <div class="pt-3 mt-auto border-t border-gray-100 text-left space-y-3">
        <p class="text-[11px] text-gray-500 leading-relaxed">
          <span class="font-bold text-[#2c3e50] block mb-0.5">Outcome:</span>
          Bias-reduced, forward-looking talent signals.
        </p>

        <!-- Button Logic for Step 3 -->
        <button
          @click="selectStep(3)"
          class="w-full py-3 px-4 rounded-lg text-sm font-bold transition-all duration-200 border-2 flex items-center justify-center gap-2"
          :class="{
            'btn-grid-completed': steps[2].completed || 3 < currentStep,
            'btn-grid-start': 3 === currentStep && !steps[2].completed,
            'bg-white border-gray-200 text-gray-300 cursor-not-allowed hover:bg-gray-50': 3 > currentStep
          }"
          :disabled="3 > currentStep"
        >
          <span v-if="steps[2].completed || 3 < currentStep" class="flex items-center gap-2">
            Completed
            <div class="bg-white rounded-full flex items-center justify-center w-5 h-5">
              <feather-icon icon="CheckIcon" class="w-3 h-3 text-green" />
            </div>
          </span>
          <span v-else-if="3 === currentStep" class="flex items-center gap-2">
            Start Now <feather-icon icon="ArrowRightIcon" class="w-4 h-4" />
          </span>
          <span v-else class="flex items-center gap-2">
            Pending <feather-icon icon="LockIcon" class="w-3 h-3" />
          </span>
        </button>
      </div>
    </div>

    <!-- CARD 4 (Step 4) -->
    <div
      class="bg-white rounded-xl border border-gray-200 p-5 flex flex-col h-full hover:border-[#f5bb00] hover:shadow-lg transition-all duration-300 relative group shadow-sm text-left"
      :class="{ 'ring-2 ring-[#f5bb00] border-[#f5bb00]': currentStep === 4 }">
      
      <div class="flex justify-between items-start mb-3">
        <div class="text-[#2c3e50] group-hover:text-[#f5bb00] transition-colors">
          <feather-icon icon="MapIcon" class="w-8 h-8" />
        </div>
        <span class="text-xs font-bold text-gray-300 select-none">04</span>
      </div>

      <div class="min-h-[48px] flex items-start mb-1">
        <h3 class="text-lg font-bold text-[#2c3e50] text-left leading-snug">
          Generate Talent Plans
        </h3>
      </div>

      <div class="min-h-[24px] mb-4">
        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide text-left">
          TURN INSIGHTS INTO EXECUTION
        </p>
      </div>

      <div class="mb-4">
        <div class="min-h-[32px] flex items-start mb-2">
          <p class="text-[13.5px] text-[#2c3e50] font-bold text-left leading-snug">
            Instantly unlock:
          </p>
        </div>
        <ul class="space-y-2.5 text-left">
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
             <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Role-to-talent misalignment insights</span>
          </li>
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
             <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Succession readiness & risk mapping</span>
          </li>
          <li class="flex items-start gap-3 text-[13px] text-gray-600 leading-normal">
             <div class="shrink-0 mt-0.5 text-[#f5bb00]">
               <feather-icon icon="CheckCircleIcon" class="w-5 h-5" />
            </div>
            <span class="flex-1">Hiring, mobility & development plans</span>
          </li>
        </ul>
      </div>

      <div class="pt-3 mt-auto border-t border-gray-100 text-left space-y-3">
        <p class="text-[11px] text-gray-500 leading-relaxed">
          <span class="font-bold text-[#2c3e50] block mb-0.5">Outcome:</span>
          Data-backed talent plans for hiring, upskilling, succession & growth.
        </p>

        <!-- Button Logic for Step 4 -->
        <button
          @click="selectStep(4)"
          class="w-full py-3 px-4 rounded-lg text-sm font-bold transition-all duration-200 border-2 flex items-center justify-center gap-2"
          :class="{
            'btn-grid-completed': steps[3].completed || 4 < currentStep,
            'btn-grid-start': 4 === currentStep && !steps[3].completed,
            'bg-white border-gray-200 text-gray-300 cursor-not-allowed hover:bg-gray-50': 4 > currentStep
          }"
          :disabled="4 > currentStep"
        >
          <span v-if="steps[3].completed || 4 < currentStep" class="flex items-center gap-2">
            Completed
            <div class="bg-white rounded-full flex items-center justify-center w-5 h-5">
              <feather-icon icon="CheckIcon" class="w-3 h-3 text-green" />
            </div>
          </span>
          <span v-else-if="4 === currentStep" class="flex items-center gap-2">
            Start Now <feather-icon icon="ArrowRightIcon" class="w-4 h-4" />
          </span>
          <span v-else class="flex items-center gap-2">
            Pending <feather-icon icon="LockIcon" class="w-3 h-3" />
          </span>
        </button>
      </div>
    </div>
             </div>
          </div>

          <!-- Active Step Content -->
          <div v-else-if="isStepSelected" class="w-full animate-fade-in-up">
            <div class="text-center mb-8 pt-4">
              <h2 class="text-[28px] leading-tight font-semibold text-[#2c3e50] mb-3">
                 {{ currentStep <= 4 ? steps[currentStep - 1].title : steps[3].title }}
              </h2>
              <p class="text-[#666] text-base max-w-3xl mx-auto">
                 {{ currentStep <= 4 ? steps[currentStep - 1].description : steps[3].description }}
              </p>
            </div>

            <!-- STEP 1: Design Organization (Unchanged) -->
            <div v-if="currentStep === 1" class="max-w-5xl mx-auto">
               <!-- ... existing step 1 content ... -->
               <div class="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
                <div class="mb-8">
                  <h3 class="text-2xl font-semibold text-[#2c3e50] mb-3">Organization Structure</h3>
                  <p class="text-[#666] max-w-lg mx-auto">
                    We have initialized your organization workspace. Click below to customize your departments, roles,
                    and hierarchy settings in the Organization Planner.
                  </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-10">
                  <div @click="navigateToPlan"
                    class="p-6 rounded-xl border border-gray-100 bg-gray-50 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer group">
                    <feather-icon icon="FolderPlusIcon"
                      class="w-8 h-8 text-[#f5bb00] mb-3 group-hover:scale-110 transition-transform" />
                    <h4 class="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">Departments</h4>
                    <p class="text-sm text-gray-500">Manage business units</p>
                  </div>
                  <div @click="navigateToPlan"
                    class="p-6 rounded-xl border border-gray-100 bg-gray-50 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer group">
                    <feather-icon icon="UsersIcon"
                      class="w-8 h-8 text-[#f5bb00] mb-3 group-hover:scale-110 transition-transform" />
                    <h4 class="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">Roles</h4>
                    <p class="text-sm text-gray-500">Define job titles</p>
                  </div>
                  <div @click="navigateToPlan"
                    class="p-6 rounded-xl border border-gray-100 bg-gray-50 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer group">
                    <feather-icon icon="TargetIcon"
                      class="w-8 h-8 text-[#f5bb00] mb-3 group-hover:scale-110 transition-transform" />
                    <h4 class="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">Hierarchy</h4>
                    <p class="text-sm text-gray-500">Set reporting lines</p>
                  </div>
                </div>

                <div class="mt-8 text-sm text-gray-400">
                  Click any card above to open the Organization Planner, or click 'Next' to proceed to Import.
                </div>
              </div>
            </div>

            <!-- STEP 2: Import Talent Pool -->
            <div v-if="currentStep === 2" class="max-w-6xl mx-auto">
              <div class="space-y-12">

                <!-- Integration Icons -->
                <!-- <div class="text-center" v-if="!showDataMapping">
                  <p class="text-xl font-semibold mb-8 text-[#2c3e50]">Import from</p>
                  <div class="flex items-center justify-center gap-6">
                    <div class="integration-box group">
                      <img class="w-8 h-8 group-hover:scale-110 transition-transform"
                        src="@/assets/images/icons/app-marketplace/integrations/slack.svg" alt="Slack" />
                    </div>
                    <span class="text-[#666] mx-2 text-lg">or HRIS</span>
                    <div class="integration-box group">
                      <img class="w-8 h-8 group-hover:scale-110 transition-transform"
                        src="@/assets/images/icons/app-marketplace/integrations/paylocity.svg" alt="Paylocity" />
                    </div>
                    <div class="integration-box group">
                      <img class="w-8 h-8 group-hover:scale-110 transition-transform"
                        src="@/assets/images/icons/app-marketplace/integrations/hibob.svg" alt="HiBob" />
                    </div>
                    <div class="integration-box group">
                      <img class="w-8 h-8 group-hover:scale-110 transition-transform"
                        src="@/assets/images/icons/app-marketplace/integrations/personio.svg" alt="Personio" />
                    </div>
                  </div>
                </div> -->

                <!-- MAIN SELECTION VIEW (Side-by-Side) -->
                <div v-if="!showDataMapping">
                  <div class="text-center mb-6">
                    <p class="text-xl font-semibold text-[#2c3e50]">Choose your upload method</p>
                  </div>
                  <div class="grid grid-cols-1 gap-8 max-w-2xl mx-auto">
                  
                  <!-- <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">

                    <div
                      class="p-8 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50 
                             hover:border-[#f5bb00] hover:bg-[#fff9e6] transition-all duration-200 cursor-pointer group h-full flex flex-col justify-center"
                      @click="triggerFileUpload" @dragover.prevent="handleDragOver" @dragleave.prevent="handleDragLeave"
                      @drop.prevent="handleFileDrop" :class="{ 'border-[#f5bb00] bg-[#fff9e6]': isDragOver }">
                      <div class="flex flex-col items-center gap-4">
                        <div
                          class="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <feather-icon icon="FileTextIcon" class="w-8 h-8 text-[#f5bb00]" />
                        </div>
                        <div>
                          <h4 class="text-lg font-bold text-[#2c3e50] mb-1">Excel or CSV</h4>
                          <p class="text-[#666] text-sm group-hover:text-[#2c3e50] transition-colors">
                            {{ uploadedFile ? uploadedFile.name : 'Drag & drop or Click to browse' }}
                          </p>
                        </div>
                        <p class="text-xs text-gray-400">Supported formats: .xlsx, .xls, .csv</p>
                      </div>
                    </div> -->

                    <!-- OPTION 2: RESUME UPLOAD -->
                    <div
                      class="p-8 border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50 
                             hover:border-[#f5bb00] hover:bg-[#fff9e6] transition-all duration-200 cursor-pointer group h-full flex flex-col justify-center"
                      @click="triggerResumeFileUpload"
                      @dragover.prevent="isDragOver = true"
                      @dragleave.prevent="isDragOver = false"
                      @drop.prevent="handleResumeDrop"
                      :class="{ 'border-[#f5bb00] bg-[#fff9e6]': isDragOver }">
                      <div class="flex flex-col items-center gap-4">
                        <div
                          class="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <feather-icon icon="FileIcon" class="w-8 h-8 text-[#f5bb00]" />
                        </div>
                        <div>
                          <h4 class="text-lg font-bold text-[#2c3e50] mb-1">Resume Files</h4>
                          <p class="text-[#666] text-sm group-hover:text-[#2c3e50] transition-colors">
                            Drag & drop or Click to browse
                          </p>
                        </div>
                        <p class="text-xs text-gray-400">Supported formats: PDF, .txt, .doc, .docx</p>
                      </div>
                    </div>
                  </div>

                  <!-- Hidden File Input for Excel -->
                  <!-- <input ref="fileInput" type="file" accept=".csv,.xlsx,.xls" class="hidden" @change="handleFileSelect" /> -->
                  
                  <!-- Hidden File Input for Resumes (UPDATED ACCEPT ATTRIBUTE) -->
                  <input ref="resumeFileInput" type="file" multiple accept=".pdf,.doc,.docx,.txt" @change="handleResumeFileSelect" class="hidden" />
                </div>

                <!-- DATA MAPPING VIEW (Only shows after Excel selection) -->
                <div v-if="showDataMapping" class="max-w-3xl mx-auto">
                     <div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                        <div class="mb-8">
                          <div class="flex items-center justify-between mb-4">
                            <h3 class="text-xl font-semibold text-[#2c3e50]">Map Your Data</h3>
                            <feather-icon icon="XIcon" class="w-5 h-5 cursor-pointer" @click="resetFileUpload" />
                          </div>
                          <!-- Warning Box -->
                          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <div class="flex items-start gap-3">
                              <div class="flex-shrink-0 mt-1">
                                <svg class="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
                              </div>
                              <div class="text-align-start">
                                <h4 class="text-sm font-medium text-blue-800 mb-1">How to map your data:</h4>
                                <ul class="text-sm text-blue-700 space-y-1">
                                  <li>• <strong>Name *</strong> - Select the column containing employee names</li>
                                  <li>• <strong>Email *</strong> - Select the column containing email addresses</li>
                                  <li>• <strong>Title (Optional)</strong> - Select the column containing job titles</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Table -->
                        <div class="overflow-x-auto">
                          <table class="w-full min-w-[600px]">
                            <thead>
                              <tr class="border-b border-gray-200">
                                <th class="text-align-start py-3 px-4 font-semibold text-[#2c3e50] w-1/3">Name *</th>
                                <th class="text-align-start py-3 px-4 font-semibold text-[#2c3e50] w-1/3">Email *</th>
                                <th class="text-align-start py-3 px-4 font-semibold text-[#2c3e50] w-1/3">Title (Optional)</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr class="border-b border-gray-100">
                                <td class="py-3 px-4 w-1/3 text-align-start">
                                  <select v-model="columnMapping.name" class="w-full p-2 border border-gray-200 rounded-md" @change="validateMapping">
                                    <option value="">Select Header</option>
                                    <option v-for="header in availableHeaders" :key="header" :value="header" :disabled="isHeaderUsed(header, 'name')">{{ header }}</option>
                                  </select>
                                </td>
                                <td class="py-3 px-4 w-1/3 text-align-start">
                                  <select v-model="columnMapping.email" class="w-full p-2 border border-gray-200 rounded-md" @change="validateMapping">
                                     <option value="">Select Header</option>
                                    <option v-for="header in availableHeaders" :key="header" :value="header" :disabled="isHeaderUsed(header, 'email')">{{ header }}</option>
                                  </select>
                                </td>
                                <td class="py-3 px-4 w-1/3 text-align-start">
                                  <select v-model="columnMapping.title" class="w-full p-2 border border-gray-200 rounded-md" @change="validateMapping">
                                     <option value="">Select Header</option>
                                    <option v-for="header in availableHeaders" :key="header" :value="header" :disabled="isHeaderUsed(header, 'title')">{{ header }}</option>
                                  </select>
                                </td>
                              </tr>
                              <!-- Sample Data Loop -->
                              <tr v-for="(row, index) in sampleData" :key="index" class="border-b border-gray-50">
                                <td class="py-2 px-4 text-sm text-gray-600 w-1/3 text-align-start">{{ columnMapping.name ? row[columnMapping.name] : '-' }}</td>
                                <td class="py-2 px-4 text-sm text-gray-600 w-1/3 text-align-start">{{ columnMapping.email ? row[columnMapping.email] : '-' }}</td>
                                <td class="py-2 px-4 text-sm text-gray-600 w-1/3 text-align-start">{{ columnMapping.title ? row[columnMapping.title] : '-' }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                </div>
              </div>
            </div>

            <!-- STEP 3 & Navigation Buttons remain unchanged -->
            <div v-if="currentStep === 3" class="max-w-6xl mx-auto">
               <!-- ... existing step 3 content ... -->
               <div class="p-base">
                <vs-row>
                  <vs-col class="w-full">
                    <div class="my-4 text-white flex justify-center">
                      <div class="flex gap-3">
                        <vs-button class="hover:bg-primary hover:text-white" v-for="(config, key) in configTypes"
                          :key="key" @click="selectedConfigType = key"
                          :type="selectedConfigType === key ? 'filled' : 'border'"
                          :color="selectedConfigType === key ? 'primary' : 'gray'">
                          {{ config.title }}
                        </vs-button>
                      </div>
                    </div>
                  </vs-col>
                </vs-row>

                <vs-row>
                  <vs-col class="w-full">
                    <div class="space-y-8 mt-8">
                      <div v-for="(criteria, categoryName) in getCurrentConfig()" :key="categoryName"
                        class="bg-white rounded-lg p-6">
                        <h3 class="text-lg font-semibold mb-6">{{ categoryName || 'Special Factors' }}</h3>

                        <div class="space-y-4">
                          <div v-for="(criterion, index) in criteria" :key="index" class="criterion-item border-b border-gray-100 last:border-b-0 py-3">
                            
                            <!-- This new structure fixes alignment and adds the Edit button -->
                            <vs-row vs-align="flex-start" vs-type="flex" vs-justify="space-between" class="w-full">
                              
                              <!-- Left Column: Title, Key, Description (Now correctly left-aligned) -->
                              <vs-col vs-w="8">
                                <div class="text-left pr-4">
                                  <div class="flex items-baseline gap-2 mb-1">
                                    <h4 class="font-medium text-base text-[#2c3e50]">{{ criterion.title }}</h4>
                                    <span class="text-xs text-gray-400">({{ criterion.key }})</span>
                                  </div>
                                  <p v-if="criterion.desc" class="text-sm text-gray-500 italic">{{ criterion.desc }}</p>
                                </div>
                              </vs-col>

                              <!-- Right Column: Controls -->
                              <vs-col vs-w="4">
                                <div class="flex items-center justify-end gap-2 h-full">
                                  
                                  <!-- Weight Display -->
                                  <span class="text-sm text-gray-500">Weight:</span>
                                    <vs-input 
                                      type="number" 
                                      v-model="criterion.value" 
                                      class="w-20" 
                                    />
                                    <span class="text-sm">%</span>

                                  <!-- UPDATED: Edit Button with Tooltip -->
                                  <vx-tooltip text="Edit Criteria" position="top">
                                    <vs-button 
                                      type="flat" 
                                      color="primary"
                                      icon-pack="feather" 
                                      icon="icon-edit-2"
                                      class="w-8 h-8 rounded-lg hover:bg-primary-light"
                                      @click="openEditCriterionModal(categoryName, index)" 
                                    />
                                  </vx-tooltip>
                                  
                                  <!-- UPDATED: Delete Button with Tooltip -->
                                  <vx-tooltip text="Delete Criteria" position="top">
                                    <vs-button 
                                      type="flat" 
                                      color="danger" 
                                      icon="delete"
                                      class="w-8 h-8 rounded-lg hover:bg-danger-light"
                                      @click="removeCriterion(categoryName, index)" 
                                    />
                                  </vx-tooltip>

                                </div>
                              </vs-col>
                            </vs-row>
                          </div>

                          <vs-button type="border" color="primary" class="w-full mt-4" @click="openCriterionModal(categoryName)">
                            + Add Criteria
                          </vs-button>
                        </div>
                      </div>
                    </div>
                  </vs-col>
                </vs-row>

                <!-- Updated Button Row with Corner Navigation -->
                <vs-row class="mt-8">
                  <vs-col class="w-full relative flex items-center justify-center min-h-[50px]">
                    
                    <!-- Left Arrow Button (Absolute Left) -->
                    <div v-if="!isFirstConfig" class="absolute left-0">
                      <vx-tooltip text="Evaluation Previous Metric" position="right">
                        <div 
                          class="w-10 h-10 rounded-full cursor-pointer flex items-center justify-center border border-gray-300 text-gray-500 hover:border-primary hover:bg-primary hover:text-white transition-all duration-200 shadow-sm"
                          @click="navigateConfig('prev')"
                        >
                          <feather-icon icon="ArrowLeftIcon" class="w-5 h-5" />
                        </div>
                      </vx-tooltip>
                    </div>

                    <!-- Center Add Category Button -->
                    <vs-button type="border" color="primary" icon="folder_add" @click="openCategoryModal">
                      Add New Category
                    </vs-button>

                    <!-- Right Arrow Button (Absolute Right) -->
                    <div v-if="!isLastConfig" class="absolute right-0">
                      <vx-tooltip text="Evaluation Next Metric" position="left">
                        <div 
                          class="w-10 h-10 rounded-full cursor-pointer flex items-center justify-center border border-gray-300 text-gray-500 hover:border-primary hover:bg-primary hover:text-white transition-all duration-200 shadow-sm"
                          @click="navigateConfig('next')"
                        >
                          <feather-icon icon="ArrowRightIcon" class="w-5 h-5" />
                        </div>
                      </vx-tooltip>
                    </div>

                  </vs-col>
                </vs-row>
              </div>
            </div>

            <!-- Navigation Buttons -->
            <div v-if="isStepSelected" class="mt-8 border-t border-gray-100 pt-8">
              <div class="flex items-center justify-between max-w-4xl mx-auto">
                
                <!-- PREVIOUS BUTTON: Hidden if isStandalone is true -->
                <div :class="{ 'invisible': isStandalone }">
                  <vs-button 
                    type="border" 
                    color="#4b5563"
                    class="px-6 py-2.5 hover:bg-gray-50 transition-all duration-200" 
                    @click="prevStep"
                    :disabled="currentStep === 1">
                    <template #icon>
                      <feather-icon icon="ChevronLeftIcon" class="w-5 h-5" />
                    </template>
                    Previous
                  </vs-button>
                </div>
                
                <!-- NEXT / FINISH BUTTON -->
                <vs-button
                  type="filled"
                  color="#f5bb00"
                  class="px-8 py-2.5 shadow-md hover:shadow-lg transform active:scale-95 transition-all duration-200"
                  @click="nextStep">
                  
                  <span class="font-bold text-white text-base">
                    <!-- Dynamic Text Logic -->
                    <template v-if="currentStep === 2 && showDataMapping">
                       Import & {{ isStandalone ? 'Finish' : 'Next' }}
                    </template>
                    <template v-else-if="isStandalone">
                       Done
                    </template>
                    <template v-else>Next</template>
                  </span>

                  <template #icon>
                    <feather-icon icon="ChevronRightIcon" class="w-5 h-5 text-white" />
                  </template>
                </vs-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Category & Criteria Modals (Unchanged) -->
    <vs-popup class="popup-card" v-model="showCategoryModal" title="Add New Category" :active.sync="showCategoryModal"
      :button-close-hidden="true">
      <div class="p-4">
        <vs-input v-model="newCategory" label="Category Name" placeholder="Enter category name" class="w-full mb-4" />
        <div class="flex justify-end gap-2 mt-6">
          <vs-button type="border" color="dark" @click="closeCategoryModal">
            Cancel
          </vs-button>
          <vs-button color="primary" @click="addCategory">
            Add Category
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <vs-popup 
  class="popup-card" 
  v-model="showCriteriaModal" 
  :title="isEditing ? 'Edit Criteria' : 'Add New Criteria'" 
  :active.sync="showCriteriaModal"
>
  <div class="p-4">
    <vs-input v-model="newCriteria.title" label="Title" placeholder="Enter criteria title" class="w-full mb-4" />
    <vs-input v-model="newCriteria.key" label="Key" placeholder="Enter criteria key (e.g., KP)" class="w-full mb-4" />
    <vs-input v-model="newCriteria.desc" label="Description" placeholder="Enter criteria description" class="w-full mb-4" />

    <div class="flex justify-end gap-2 mt-6">
      <vs-button type="border" color="dark" @click="closeCriterionModal">
        Cancel
      </vs-button>
      <vs-button color="primary" @click="isEditing ? updateCriterion() : addCriterion()">
        {{ isEditing ? 'Save Changes' : 'Add' }}
      </vs-button>
    </div>
  </div>
</vs-popup>
  </section>
</template>

<script>
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, UploadCloudIcon, FileTextIcon, FileIcon, Trash2Icon, PlusIcon, PlusCircleIcon, FolderPlusIcon, XIcon, CpuIcon, UsersIcon, TargetIcon, LayoutIcon, GridIcon, ListIcon, MapIcon, TrendingUpIcon, ArrowRightIcon, LockIcon, ArrowLeftIcon } from "vue-feather-icons";
import { CheckCircleIcon } from "vue-feather-icons";

export default {
  data() {
    return {
      // --- CHANGE HERE: Set default to 'simple' instead of 'detailed' ---
      isStandalone: false,
      viewMode: 'simple', 
      
      slug: "",
      orgName: "",
      about: "",
      slugLoading: false,
      uploadedFile: null,
      uploadType: 'excel',
      isDragOver: false,
      showDataMapping: false,
      isEditing: false,
    editingCriterion: {
        category: null,
        index: -1
    },
      //showResumeProgress: false,
      availableHeaders: [],
      columnMapping: {
        name: '',
        email: '',
        title: ''
      },
      // ... rest of your data properties remain unchanged
      sampleData: [],
      allData: [],
      steps: [
        {
          id: 1,
          title: " Define Organization Roles",
          description: "Structure teams around value-critical roles.",
          completed: false
        },
        {
          id: 2,
          title: " Add Talent",
          description: "Import people data from any source.",
          completed: false
        },
        {
          id: 3,
          title: "Evaluate",
          description: "Set up your indicators to evaluate talent based on what matters most to your organization.",
          completed: false
        },
        {
          id: 4,
          title: "Plan",
          description: "Turn insights into fitment, development & succession actions with talent plan for each role.",
          method: "link",
          link: "/reports",
          completed: false
        }
      ],
      detailedSteps: [
        {
          id: 1,
          title: "Define KVDRs-Critical Roles",
          subtitle: "Align talent to what drives business",
          intro: "Map your organization by identifying:",
          points: [
            "Core functions & departments",
            "Key Value Delivery Roles (roles that materially impact strategy & growth)",
            "Reporting lines and role criticality"
          ],
          outcome: "A business-aligned organizational blueprint with a look of an org chart."
        },
        {
          id: 2,
          title: "Build Your Talent Ecosystem",
          subtitle: "Single source of truth for your people data",
          intro: "Bring your workforce into GetBoarded by:",
          points: [
            "Uploading CVs, Excel, or role profiles",
            "Connecting your HRIS (if available)",
            "Enrichment with skills, abilities & aspirations"
          ],
          outcome: "A live, searchable talent pool ready for analysis; internal & external."
        },
        {
          id: 3,
          title: "Evaluate Career Trajectories",
          subtitle: "Beyond performance & potential stats",
          intro: "Configure how talent is assessed by:",
          points: [
            "Using GetBoarded's trajectory models (Fitment, Performance, Succession)",
            "Customizing parameters aligned to your business context",
            "Weighing signals that matter now and in the future"
          ],
          outcome: "Bias-reduced, forward-looking talent signals."
        },
        {
          id: 4,
          title: "Generate Talent Plans",
          subtitle: "Turn insights into execution",
          intro: "Instantly unlock:",
          points: [
            "Role-to-talent misalignment insights",
            "Succession readiness & risk mapping",
            "Hiring, mobility & development plans"
          ],
          outcome: "Data-backed talent plans for hiring, upskilling, succession & growth."
        }
      ],
      currentStep: 1,
      isStepSelected: false,
      selectedConfigType: 'appraisal',
      showCriteriaModal: false,
      showCategoryModal: false,
      selectedCategory: '',
      newCategory: '',
      newCriteria: {
        title: '',
        key: '',
        desc: '',
        value: '10'
      },
      configurations: {
        appraisal: {
          'GOALS': [
            { title: 'KPIs', value: '10', key: 'KP', desc: '(Reflect on the goals set and assess the extent to which they were achieved)' },
            { title: 'QUALITY', value: '10', key: 'QU', desc: '(Evaluate the quality and accuracy of your contribution, considering any feedback received)' },
            { title: 'IMPACT', value: '10', key: 'IM', desc: '(Reflect on how your work contributed to the overall goals and success measures)' },
          ],
          'KNOWLEDGE': [
            { title: 'STRENGTHS', value: '5', key: 'SR', desc: '(Reflect on key strengths and areas for improvement in context of the role.)' },
            { title: 'LEARNING', value: '5', key: 'LR', desc: '(Evaluate efforts to acquire skills or knowledge through training, courses, self-study.)' },
            { title: 'APPLICATION OF SKILLS', value: '5', key: 'AS', desc: '(Did you get opportunity to apply acquired skills or knowledge to your work)' },
          ],
          'BEHAVIOUR': [
            { title: 'COMMUNICATION', value: '5', key: 'CM', desc: '(Reflect on your ability to listen actively, and communicate appropriately in professional dialogue.)' },
            { title: 'COLLABORATION', value: '5', key: 'CL', desc: '(Assess your ability to maintain positive working relationships, and contribute to teamwork.)' },
            { title: 'SUPPORT', value: '5', key: 'SR', desc: '' },
          ],
          '': [
            { title: 'X-FACTOR', value: '10', key: 'XF', desc: '(Anything special beyond the scope of defined indicators. Scarce skills, Special Referral, etc.)' }
          ]
        },
        fitment: {
          'ABILITY': [
            { title: 'FUNCTIONAL READINESS', value: '10', key: 'FR', desc: 'Has functional skills as per the role' },
            { title: 'ROLE KNOWLEDGE', value: '10', key: 'RK', desc: 'Has relevant role knowledge to perform tasks' },
            { title: 'RELEVANT EXPERIENCE', value: '10', key: 'RE', desc: 'Has experience to manage optimum performance' },
          ],
          'ASPIRATION': [
            { title: 'INTEREST', value: '10', key: 'IR', desc: 'Interest (Do they want to move?)' },
            { title: 'MOBILITY', value: '10', key: 'MO', desc: 'Mobility (Can they move?)' },
            { title: 'SUPPORT', value: '10', key: 'SP', desc: 'Has support to manage optimal performance' },
          ],
          'ATTITUDE': [
            { title: 'BEHAVIOURAL ATTRIBUTES', value: '10', key: 'BA', desc: 'Has behavioural attributes matching the need of the role' },
            { title: 'COGNITIVE COMPLEXITY', value: '10', key: 'CC', desc: 'Has the cognitive complexity to understand the needS' },
            { title: 'LEARNING APTITUDE', value: '10', key: 'LA', desc: 'HaS learning aptitude to quickly learn role specific' },
          ],
          '': [
            { title: 'X-FACTOR', value: '10', key: 'XF', desc: '(Anything special beyond the scope of defined indicators. Scarce skills, Special Referral, etc.)' }
          ]
        },
        succession: {
          'AGILITY': [
            { title: 'TRANSITION ABILITY', value: '10', key: 'TA', desc: '' },
            { title: 'KNOWLEDGE READINESS', value: '10', key: 'KR', desc: '' },
            { title: 'RELEVANT EXPERIENCE', value: '10', key: 'RE', desc: '' },
          ],
          'ADAPTABILITY': [
            { title: 'EMOTIONAL INTELLIGENCE', value: '10', key: 'EI', desc: '' },
            { title: 'RESILIENCE', value: '10', key: 'RL', desc: '' },
            { title: 'LEARNING APTITUDE', value: '10', key: 'LA', desc: '' },
          ],
          '': [
            { title: 'X-FACTOR', value: '10', key: 'XF', desc: '(Anything special beyond the scope of defined indicators. Scarce skills, Special Referral, etc.)' }
          ]
        },
        performance: {
          'GOALS': [
            { title: 'KPIs', value: '10', key: 'KP', desc: '(Reflect on the goals set and assess the extent to which they were achieved)' },
            { title: 'QUALITY', value: '10', key: 'QU', desc: '(Evaluate the quality and accuracy of your contribution, considering any feedback received)' },
            { title: 'IMPACT', value: '10', key: 'IM', desc: '(Reflect on how your work contributed to the overall goals and success measures)' },
          ],
          'SKILL': [
            { title: 'IQ', value: '10', key: 'IQ', desc: 'Intelligence Quotient to operate as per the complexity of the role' },
            { title: 'TQ', value: '10', key: 'TQ', desc: 'Technical Quotient to have necessary skills to work on tasks' },
          ],
          '': [
            { title: 'X-FACTOR', value: '10', key: 'XF', desc: '(Anything special beyond the scope of defined indicators. Scarce skills, Special Referral, etc.)' }
          ]
        }
      }
    };
  },
  components: {
    // ... components unchanged ...
    CheckCircleIcon,
    CheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    UploadCloudIcon,
    FileTextIcon,
    FileIcon,
    Trash2Icon,
    PlusIcon,
    PlusCircleIcon,
    FolderPlusIcon,
    XIcon,
    CpuIcon,
    UsersIcon,
    TargetIcon,
    LayoutIcon,
    GridIcon,
    ListIcon,
    MapIcon,
    TrendingUpIcon,
    ArrowRightIcon,
    LockIcon,
    ArrowLeftIcon
  },
  methods: {
    // ... selectStep, navigateConfig, save/load steps unchanged ...
    selectStep(stepId) {
      if (this.currentStep + 1 < stepId) {
        return;
      }

      this.currentStep = stepId;

      if (this.currentStep >= 3 && this.steps[stepId - 1].method === "link") {
        this.steps[stepId - 1].completed = true;
        this.currentStep = 5; 
        this.saveStepToStorage();
        this.$router.push(`/organization/${this.orgData.slug}${this.steps[stepId - 1].link}`);
        return;
      } else {
        this.isStepSelected = true;
        this.saveStepToStorage();
      }

    },

    openEditCriterionModal(category, index) {
  this.isEditing = true;
  this.editingCriterion = { category, index };
  
  // Get the criterion object we want to edit
  const criterionToEdit = this.configurations[this.selectedConfigType][category][index];
  
  // IMPORTANT: Create a *copy* of the object to populate the modal.
  // This prevents changes from reflecting in the list before you hit "Save".
  this.newCriteria = { ...criterionToEdit }; 
  
  this.showCriteriaModal = true;
},

// --- NEW METHOD ---
updateCriterion() {
  if (this.newCriteria.title && this.newCriteria.key) {
    const { category, index } = this.editingCriterion;
    
    // Find the original criterion in your main data object
    const originalCriterion = this.configurations[this.selectedConfigType][category][index];

    // Use Object.assign to update its properties with the new values from the modal
    Object.assign(originalCriterion, this.newCriteria);
    
    this.closeCriterionModal();
  }
},

// --- UPDATE THIS METHOD ---
openCriterionModal(categoryName) {
  // Ensure we are in "add" mode, not "edit" mode
  this.isEditing = false; 
  this.selectedCategory = categoryName;
  this.showCriteriaModal = true;
},

// --- UPDATE THIS METHOD ---
closeCriterionModal() {
  this.showCriteriaModal = false;
  
  // Reset all state for both adding and editing
  this.isEditing = false;
  this.editingCriterion = { category: null, index: -1 };
  this.selectedCategory = '';
  this.newCriteria = {
    title: '',
    key: '',
    desc: '',
    value: '10'
  };
},

    // Add this inside methods: { ... }

async saveConfigurationToServer() {
  try {
    this.$vs.loading();
    
    // 1. Save locally for the Org Chart UI
    this.saveConfigsToStorage();

    // 2. Save to Database for the AI Backend
    const apiToken = localStorage.getItem('accessToken');
    const API_BASE_URL = "https://api-core-gateway.getboarded.com"; // From your logs

    const response = await fetch(`${API_BASE_URL}/organization/${this.orgData._id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiToken}`
      },
      body: JSON.stringify({
        // This structure is what the Python backend is looking for
        settings: {
           evaluation_metrics: this.configurations
        }
      })
    });

    if (!response.ok) {
       const errData = await response.json();
       console.error("Server rejected save:", errData);
       throw new Error("Backend failed to save settings");
    }

    this.$vs.notify({
      title: 'Success',
      text: 'AI Models updated with your custom metrics!',
      color: 'success'
    });
    
    return true;
  } catch (error) {
    console.error("Save Error:", error);
    this.$vs.notify({
      title: 'Warning',
      text: 'Metrics saved locally, but AI Backend might use defaults until server syncs.',
      color: 'warning'
    });
    return true; // Still allow 'Next'
  } finally {
    this.$vs.loading.close();
  }
},
    
    navigateConfig(direction) {
      const types = this.orderedConfigKeys;
      const currentIndex = types.indexOf(this.selectedConfigType);
      let nextIndex;

      if (direction === 'next') {
        if (currentIndex < types.length - 1) {
          nextIndex = currentIndex + 1;
        } else {
          return; // Stop if at end
        }
      } else {
        if (currentIndex > 0) {
          nextIndex = currentIndex - 1;
        } else {
          return; // Stop if at start
        }
      }
      
      this.selectedConfigType = types[nextIndex];
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    saveStepToStorage() {
      // Prevent saving progress if user is just using the standalone tool
      if (this.isStandalone) return; 

      if (this.orgData && this.orgData.slug) {
        localStorage.setItem(`onboardingStep_${this.orgData.slug}`, this.currentStep.toString());
      }
    },
    loadStepFromStorage() {
      if (this.orgData && this.orgData.slug) {
        const key = `onboardingStep_${this.orgData.slug}`;
        const savedStep = localStorage.getItem(key);
        
        if (savedStep) {
          this.currentStep = parseInt(savedStep);
        } else {
          this.currentStep = 1; 
        }

        this.steps.forEach(step => {
            if (step.id < this.currentStep) {
                step.completed = true;
            } else {
                step.completed = false;
            }
        });
      }
    },

    validateData() {
      // ... unchanged ...
      if (!this.columnMapping.name || !this.columnMapping.email) {
        this.$vs.notify({
          title: 'Please map all required fields before proceeding.',
          color: 'danger'
        });
        return false;
      }

      const invalidRows = this.sampleData.filter(row => !row[this.columnMapping.email].match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) || !row[this.columnMapping.name]);

      if (invalidRows.length === this.sampleData.length) {
        this.$vs.notify({
          title: 'No users could be imported',
          text: 'Reason: The email or name is missing or invalid',
          color: 'danger'
        });
        return false;
      }

      this.sampleData = this.sampleData.filter(row => !invalidRows.includes(row));
      if (invalidRows.length > 0) {
        this.$vs.notify({
          title: 'Some users could not be imported',
          text: 'Reason: The email or name is missing or invalid',
          color: 'warning'
        });
        return false;
      }

      return true;
    },
    showSteps() {
      this.isStepSelected = false;
    },
    async nextStep() {
      if (this.currentStep === 1) {
        this.steps[this.currentStep - 1].completed = true;
        this.currentStep++;
        this.isStepSelected = true;
        this.saveStepToStorage();
      }  else if (this.currentStep === 2) {
        
        // CASE 1: USER IS IN MIDDLE OF EXCEL MAPPING
        if (this.showDataMapping) {
          if (!this.validateData()) {
            return; // Stop if mapping invalid
          }
          try {
            this.$vs.loading();
            // Process Excel Data
            await this.allData.forEach(async (row) => {
              const user = {
                "firstName": row[this.columnMapping.name],
                "email": row[this.columnMapping.email],
                "roleTitle": row[this.columnMapping.title] || '',
                "status": "ACTIVE"
              };
              const payload = {
                user,
                orgId: this.orgData._id
              }
              await this.$store.dispatch('organization/importUser', payload);
            });
            
            // Cleanup and Move Next
            //this.showDataMapping = false;
            if (this.isStandalone) {
               this.$vs.notify({title:'Success', text:'Talent imported successfully', color:'success'});
               this.showDataMapping = false; 
               // Redirect to dashboard or whereever you want after adding talent
               this.$router.push(`/organization/${this.orgData.slug}/plan`); 
               return;
            }
            this.steps[this.currentStep - 1].completed = true;
            this.currentStep++;
            this.isStepSelected = true;
            this.saveStepToStorage();
            this.$vs.loading.close();
            
          } catch (error) {
            this.$vs.loading.close();
            console.error(error);
          }
        } 
        // CASE 2: MAIN VIEW or RESUME PROGRESS DONE
        else {
          // Just move to next step
          if (this.isStandalone) {
             this.$router.push(`/organization/${this.orgData.slug}/plan`);
             return;
           }
          this.steps[this.currentStep - 1].completed = true;
          this.currentStep++;
          this.isStepSelected = true;
          this.saveStepToStorage();
        } } else if (this.currentStep === 3) {
        const saved = await this.saveConfigurationToServer();
        if (!saved) return;
        this.steps[this.currentStep - 1].completed = true;

    const nextStepIndex = 3; 
    if (this.steps[nextStepIndex] && this.steps[nextStepIndex].method === "link") {
          this.steps[nextStepIndex].completed = true;
          this.currentStep = 5; 
          this.saveStepToStorage();

          this.$router.push({
              path: `/organization/${this.orgData.slug}${this.steps[nextStepIndex].link}`,
              query: { onboarding_active: 'true' } 
          });
          return;
    }

    this.isStepSelected = false;
    if (this.currentStep < this.steps.length) {
      this.currentStep++;
      this.isStepSelected = true;
    }
    this.saveStepToStorage();

  }  else if (this.currentStep < this.steps.length) {
        this.steps[this.currentStep - 1].completed = true;
        this.currentStep++;
        this.isStepSelected = true;
        this.saveStepToStorage();
      }
    },
    // ... prevStep, completeStep, criterion methods unchanged ...
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.isStepSelected = true;
        this.saveStepToStorage();
      }
    },
    completeStep() {
      this.steps[this.currentStep - 1].completed = true;
      this.nextStep();
    },
    getCurrentConfig() {
      return this.configurations[this.selectedConfigType] || {};
    },
    openCriterionModal(categoryName) {
      this.selectedCategory = categoryName;
      this.showCriteriaModal = true;
    },
    closeCriterionModal() {
      this.showCriteriaModal = false;
      this.selectedCategory = '';
      this.newCriteria = {
        title: '',
        key: '',
        desc: '',
        value: '10'
      };
    },
    addCriterion() {
      if (this.newCriteria.title && this.newCriteria.key) {
        if (!this.configurations[this.selectedConfigType][this.selectedCategory]) {
          this.configurations[this.selectedConfigType][this.selectedCategory] = [];
        }
        this.configurations[this.selectedConfigType][this.selectedCategory].push({
          title: this.newCriteria.title,
          key: this.newCriteria.key,
          desc: this.newCriteria.desc,
          value: this.newCriteria.value
        });
        this.closeCriterionModal();
      }
    },
    removeCriterion(categoryName, index) {
      this.configurations[this.selectedConfigType][categoryName].splice(index, 1);
    },
    openCategoryModal() {
      this.showCategoryModal = true;
    },
    closeCategoryModal() {
      this.showCategoryModal = false;
      this.newCategory = '';
    },
    addCategory() {
      if (this.newCategory.trim()) {
        this.$set(this.configurations[this.selectedConfigType], this.newCategory.toUpperCase(), []);
        this.closeCategoryModal();
      }
    },
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    // REMOVED openResumeUploadModal and closeResumeUpload
    // NEW METHODS FOR RESUMES:

    triggerResumeFileUpload() {
      this.$refs.resumeFileInput.click();
    },
    // closeResumeProgress() {
    //   this.showResumeProgress = false;
    // },
    handleResumeFileSelect(event) {
      if (event.target.files) {
        this.handleResumeFiles(event.target.files);
      }
    },
    handleResumeDrop(event) {
      this.isDragOver = false;
      if (event.dataTransfer.files) {
        this.handleResumeFiles(event.dataTransfer.files);
      }
    },
    async handleResumeFiles(files) {
      const MAX_FILES = 20;
      const MAX_FILE_SIZE_MB = 10; // Increased to 10MB to accommodate Docx images sometimes

      // Allow PDF, DOC, DOCX, TXT
      const allowedExtensions = /\.(pdf|doc|docx|txt)$/i;
      
      let validFiles = Array.from(files).filter(file => allowedExtensions.test(file.name));

      if (validFiles.length !== files.length) {
        this.$vs.notify({
          title: 'Invalid File Types',
          text: 'Some files were ignored. Supported formats: PDF, DOC, DOCX, TXT.',
          color: 'warning',
          time: 5000
        });
      }

      if (validFiles.length === 0) {
        this.$vs.notify({
          title: 'No Valid Files',
          text: 'Please select PDF, DOC, DOCX, or TXT files to upload.',
          color: 'warning'
        });
        return;
      }

      if (validFiles.length > MAX_FILES) {
        this.$vs.notify({
          title: 'Too Many Files',
          text: `You can only upload a maximum of ${MAX_FILES} files at a time.`,
          color: 'warning'
        });
        return;
      }

      for (const file of validFiles) {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          this.$vs.notify({
            title: 'File Too Large',
            text: `"${file.name}" is larger than the ${MAX_FILE_SIZE_MB}MB limit.`,
            color: 'warning'
          });
          return;
        }
      }

      this.$vs.loading();

      const apiToken = localStorage.getItem('accessToken');
      if (!apiToken) {
        this.$vs.notify({
          title: 'Authentication Error',
          text: 'Could not find access token. Please log in again.',
          color: 'danger',
          time: 6000
        });
        this.$vs.loading.close();
        return;
      }

      const formData = new FormData();
      formData.append('api_token', apiToken);
      formData.append('organization_id', this.orgData._id);
      validFiles.forEach(file => formData.append('files', file));

      try {
        const API_BASE_URL = process.env.VUE_APP_API_URL;
        const response = await fetch(`${API_BASE_URL}/upload-resumes-batch/`, {
          method: 'POST',
          body: formData
        });
        const data = await response.json();

        if (!response.ok) {
          const errorDetail = (data.detail && typeof data.detail === 'string')
            ? data.detail
            : JSON.stringify(data.detail);
          throw new Error(errorDetail || `Server responded with status ${response.status}`);
        }

        this.$store.dispatch('organization/startResumeBatch', data.batch_id);

        // NOTE: showResumeProgress is removed so the view doesn't change
        
        this.$vs.notify({
          title: 'Resumes Uploaded Successfully',
          text: 'Your resumes are being processed in the background. You can upload more or click Next.',
          color: 'success',
          time: 5000
        });

        // Mark step as completed conceptually
        this.steps[this.currentStep - 1].completed = true;
        this.saveStepToStorage();
        
      } catch (error) {
        console.error('Error starting upload:', error);
        this.$vs.notify({
          title: 'Upload Failed',
          text: `Could not start the upload process. ${error.message}`,
          color: 'danger',
          time: 8000
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    // ... formatResumeStatus, handleFileSelect/Process/Read/Excel/CSV, routeToNext, etc. unchanged ...
    formatResumeStatus(status) {
      if (!status) return 'Pending';
      return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    handleDragOver(event) {
      this.isDragOver = true;
    },
    handleDragLeave(event) {
      this.isDragOver = false;
    },
    handleFileDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    processFile(file) {
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];

      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xls|xlsx|csv)$/i)) {
        this.$vs.notify({
          title: 'Invalid File Type',
          text: 'Please upload an Excel (.xls, .xlsx) or CSV file.',
          color: 'danger'
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.$vs.notify({
          title: 'File Too Large',
          text: 'Please upload a file smaller than 10MB.',
          color: 'danger'
        });
        return;
      }

      this.uploadedFile = file;
      this.$vs.notify({
        title: 'File Uploaded',
        text: `${file.name} has been uploaded successfully.`,
        color: 'success'
      });

      this.readFileData(file);
    },
    readFileData(file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          let rows = [];

          if (file.name.endsWith('.csv')) {
            const data = e.target.result;
            rows = this.parseCSV(data);
          } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
            this.readExcelFile(file);
            return;
          } else {
            this.$vs.notify({
              title: 'Unsupported File Type',
              text: 'Please upload a CSV or Excel file (.xls, .xlsx)',
              color: 'danger'
            });
            return;
          }

          if (rows.length > 0) {
            this.availableHeaders = Object.keys(rows[0]);
            this.allData = rows;
            this.sampleData = rows.slice(0, 5);

            this.showDataMapping = true;

            console.log('Parsed data:', rows);
          } else {
            this.$vs.notify({
              title: 'Error',
              text: 'Could not read data from file. Please check the file format.',
              color: 'danger'
            });
          }
        } catch (error) {
          console.error('Error reading file:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to read file data. Please try again.',
            color: 'danger'
          });
        }
      };

      reader.onerror = () => {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to read file. Please try again.',
          color: 'danger'
        });
      };

      reader.readAsText(file);
    },
    readExcelFile(file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          this.$vs.notify({
            title: 'Excel Support',
            text: 'Excel file support is being added. Please use CSV files for now.',
            color: 'warning'
          });

          this.availableHeaders = [
            'First Name',
            'Last Name',
            'Email',
          ];

          this.sampleData = [
            {
              'Employee ID': 'EMP001',
              'First Name': 'John',
              'Last Name': 'Doe',
              'Email': 'john.doe@company.com',
              'Department': 'IT',
              'Position': 'Developer',
              'Business Unit': 'Technology',
              'Division': 'Engineering'
            },
            {
              'Employee ID': 'EMP002',
              'First Name': 'Jane',
              'Last Name': 'Smith',
              'Email': 'jane.smith@company.com',
              'Department': 'HR',
              'Position': 'Manager',
              'Business Unit': 'Human Resources',
              'Division': 'Operations'
            }
          ];

          this.showDataMapping = true;

        } catch (error) {
          console.error('Error reading Excel file:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to read Excel file. Please try a CSV file.',
            color: 'danger'
          });
        }
      };

      reader.readAsArrayBuffer(file);
    },
    parseCSV(csvText) {
      const lines = csvText.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          rows.push(row);
        }
      }

      return rows;
    },
    resetFileUpload() {
      this.uploadedFile = null;
      this.uploadType = 'excel';
      this.showDataMapping = false;
      this.availableHeaders = [];
      this.columnMapping = {
        employeeId: '',
        name: '',
        email: ''
      };
    },
    processMapping() {
      // ... (Existing mapping logic)
      const requiredFields = ['name', 'email'];
      const missingFields = requiredFields.filter(field => !this.columnMapping[field]);

      if (missingFields.length > 0) {
        this.$vs.notify({
          title: 'Missing Required Fields',
          text: `Please map the following fields: ${missingFields.join(', ')}`,
          color: 'danger'
        });
        return;
      }
      this.$vs.notify({
        title: 'Data Mapping Complete',
        text: 'Your data has been mapped successfully!',
        color: 'success'
      });
    },
    validateMapping() {
      const values = Object.values(this.columnMapping).filter(val => val !== '');
      const uniqueValues = [...new Set(values)];

      if (values.length !== uniqueValues.length) {
        this.$vs.notify({
          title: 'Duplicate Selection',
          text: 'You cannot select the same header for multiple fields.',
          color: 'warning'
        });
      }
    },
    isHeaderUsed(header, currentField) {
      const usedHeaders = Object.values(this.columnMapping).filter(val => val !== '');
      return usedHeaders.includes(header) && this.columnMapping[currentField] !== header;
    },
    routeToNextStep() {
      const payload = {
        userId: this.userId,
        name: this.orgName,
        slug: this.slug,
        about: this.about,
      };
      this.$store.commit("onboarding/SET_ORGANIZATION", payload);
      this.$router.push(`/organization/onboard/board-type`);
    },
    createSlugSuffix() {
      let result = "";
      const characters = "abcdefghijklmnopqrstuvwxyz0123456789";
      const charactersLength = characters.length;
      let counter = 0;
      while (counter < 5) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength)
        );
        counter += 1;
      }
      return result;
    },
    async checkSlug() {
      try {
        const res = await this.$store.dispatch(
          "organization/checkSlug",
          this.slug
        );
        if (res.total > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        return true;
      }
    },
  saveConfigsToStorage() {
      if (this.orgData && this.orgData.slug) {
        localStorage.setItem(`onboardingConfigs_${this.orgData.slug}`, JSON.stringify(this.configurations));
      }
    },
    
    loadConfigsFromStorage() {
      if (this.orgData && this.orgData.slug) {
        const key = `onboardingConfigs_${this.orgData.slug}`;
        const savedConfigs = localStorage.getItem(key);
        
        if (savedConfigs) {
          try {
            this.configurations = { ...this.configurations, ...JSON.parse(savedConfigs) };
          } catch (e) {
            console.error("Error parsing saved configurations", e);
          }
        }
      }
    },
    navigateToPlan() {
      this.$router.push({
        path: `/organization/${this.orgData.slug}/plan`,
        query: { 
          onboarding_active: 'true', 
          step: '1'
        }
      });
    }
  },
  mounted() {
    this.loadStepFromStorage();
    //this.updateStepVisuals();
    if (this.$route.name === 'AddTalent' || this.$route.path.includes('/addTalent')) {
      this.isStandalone = true;
      this.currentStep = 2;       // Force Step 2
      this.isStepSelected = true; // Force Content View (skip selection list)
      
      // Optional: Set step 1 as 'completed' visually for context, though strictly not needed
      this.steps[0].completed = true; 
    } else {
      // Standard Onboarding Behavior
      this.loadStepFromStorage();
    }
  },
  computed: {
    orderedConfigKeys() {
      return Object.keys(this.configTypes); // ['appraisal', 'fitment', 'succession', 'performance']
    },
    isFirstConfig() {
      return this.selectedConfigType === this.orderedConfigKeys[0];
    },
    isLastConfig() {
      return this.selectedConfigType === this.orderedConfigKeys[this.orderedConfigKeys.length - 1];
    },
    isUserLoggedIn() {
      return this.$store.state.auth.isUserLoggedIn;
    },
    userData() {
      return this.$store.state.users.data;
    },
    userId() {
      return this.$store.state.auth.user._id;
    },
    orgData() {
      return this.$store.state.organization.orgData;
    },
    activeStep() {
      return this.steps[this.currentStep - 1];
    },
    configTypes() {
      return {
        appraisal: { title: 'Appraisal' },
        fitment: { title: 'Fitment' },
        succession: { title: 'Succession' },
        performance: { title: 'Performance' }
      };
    },
    // REMOVED resumeUploadTitle since modal is gone
    resumeProgressTitle() {
      if (this.resumeIsComplete) return 'Processing Complete';
      return 'Processing Batch...';
    },
    resumeIsComplete() {
      if (!this.resumeBatchStatus) return false;
      return ['completed', 'completed_with_errors', 'failed'].includes(this.resumeBatchStatus.status);
    },
    resumeProgressPercentage() {
      if (!this.resumeBatchStatus || !this.resumeBatchStatus.total_files) return 0;
      return (this.resumeBatchStatus.processed_count / this.resumeBatchStatus.total_files) * 100;
    },
    resumeProgressBarClass() {
      if (!this.resumeIsComplete) return 'bg-primary';
      const status = this.resumeBatchStatus?.status;
      if (status === 'completed') return 'bg-green-500';
      if (status === 'failed') return 'bg-red-500';
      if (status === 'completed_with_errors') return 'bg-yellow-500';
      return 'bg-primary';
    },
    resumeSuccessCount() {
      if (!this.resumeIsComplete || !this.resumeBatchStatus.files) return 0;
      return Object.values(this.resumeBatchStatus.files).filter(f => f.status === 'completed').length;
    },
    resumeFailureCount() {
      if (!this.resumeIsComplete || !this.resumeBatchStatus.files) return 0;
      return Object.values(this.resumeBatchStatus.files).filter(f => f.status === 'failed').length;
    },
    activeResumeBatchId() {
      return this.$store.state.organization.activeResumeBatchId;
    },
    resumeBatchStatus() {
      return this.$store.state.organization.resumeBatchStatus;
    },
  evaluationMetrics() {
    // Check if the organization has custom config saved in DB
    if (this.orgData && 
        this.orgData.settings && 
        this.orgData.settings.evaluation_metrics) {
        
        return this.orgData.settings.evaluation_metrics;
    }
    
    // Fallback to hardcoded defaults if nothing saved
    return this.defaultConfigurations; 
  }
  },
  watch: {
    async orgName() {
      var slugAvailable = false;
      this.slugLoading = true;
      this.slug = this.orgName.replace(" ", "_").toLowerCase();
      if (this.slug.length < 3) {
        return;
      }
      while (!slugAvailable) {
        slugAvailable = await this.checkSlug();
        if (!slugAvailable) {
          this.slug = `${this.slug}_${this.createSlugSuffix()}`;
        }
      }
      this.slugLoading = false;
    },
    resumeIsComplete(newValue, oldValue) {
      if (newValue && !oldValue) {
        let toastConfig = {
          title: 'Processing Complete',
          time: 5000
        };

        if (this.resumeBatchStatus.status === 'completed') {
          toastConfig.text = `All ${this.resumeSuccessCount} resume(s) were parsed successfully.`;
          toastConfig.color = 'success';
        } else if (this.resumeBatchStatus.status === 'completed_with_errors') {
          toastConfig.text = `Finished with ${this.resumeFailureCount} error(s). Please review your talent pool.`;
          toastConfig.color = 'warning';
        } else {
          toastConfig.title = 'Processing Failed';
          toastConfig.text = 'The entire batch could not be processed.';
          toastConfig.color = 'danger';
        }

        this.$vs.notify(toastConfig);
        
        // No longer auto-closing the view so user can choose to add more or click next
      }
    },
  configurations: {
      deep: true,
      handler() {
        this.saveConfigsToStorage();
      }
    },
    orgData: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && newVal.slug) {
          this.loadStepFromStorage();
          this.loadConfigsFromStorage(); 
        }
      }
    }
  },
};
</script>

<style lang="scss" scoped>
// Existing Styles
.text-align-start {
  text-align: start !important;
}

.steps-container {
  border: 1px solid #f5bc00c5 !important;
  box-shadow: 0 4px 20px -4px rgba(0, 0, 0, 0.1) !important;
}

.step-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
}

.progress-bar {
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.config-category {
  .criterion-item {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid transparent;

    &:hover {
      border-color: #e5e7eb;
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    }
  }
}

.integration-box {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    border-color: #f5bb00;
    background-color: #fff9e6;
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 10px 15px -3px rgba(245, 187, 0, 0.2);
  }
}

.upload-btn {
  background-color: rgba(var(--vs-primary), 1);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(var(--vs-primary), 0.1);

  &:hover {
    background-color: rgba(var(--vs-primary), 0.9);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(var(--vs-primary), 0.2);
  }

  &:active {
    transform: translateY(0);
    background-color: rgba(var(--vs-primary), 0.8);
  }
}

.step {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.navigation-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s;

  &.prev {
    color: #6b7280;
    border: 1px solid rgba(var(--vs-primary), 0.2);

    &:hover {
      background: rgba(var(--vs-primary), 1);
      color: white;
    }
  }

  &.next {
    color: white;
    background: rgba(var(--vs-primary), 1);

    &:hover {
      background: rgba(var(--vs-primary), 0.9);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.error-input {
  border: 1.5px solid rgba(var(--vs-danger), 1);
  box-shadow: 0 0 0 2px rgba(var(--vs-danger), 0.1);
}

.popup-card {
  .name {
    font-family: Inter;
    font-style: normal;
    font-weight: 600;
    font-size: 34px;
    line-height: 41px;
    letter-spacing: -0.02em;
    color: #30384b;
  }

  .heading {
    font-family: Inter;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 13px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #b2bac8;
  }

  .content {
    font-family: Inter;
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 23px;
    color: #798ba0;
  }

  >>>.vs-popup--header {
    display: none !important;
  }

  >>>.vs-popup {
    width: 500px !important;
  }
}

.con-vs-popup>>>.vs-popup--content {
  padding: 0 !important;
  margin: 0 !important;
  overflow-x: hidden !important;
  width: auto !important;
  border-radius: 6px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  background: white !important;
}

.con-vs-popup>>>.vs-popup {
  background: white !important;
  transform: scale(1) !important;
  opacity: 1 !important;
}

.con-vs-popup>>>.vs-popup:hover {
  background: white !important;
  transform: scale(1) !important;
  opacity: 1 !important;
}

.vs-input>>>.vs-input--input {
  box-shadow: none;
}

.vs-input>>>.vs-input--placeholder {
  top: 5px;
}

.vs-input>>>.vs-input--icon {
  font-size: 1.5rem;
  color: #97a4bf !important;
}

.upload-type-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  font-weight: 500;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -8px rgba(0, 0, 0, 0.2);
  }

  &.active {
    background: linear-gradient(135deg, #f5bb00 0%, #f5bb00 100%) !important;
    color: white !important;
    border-color: #f5bb00 !important;

    &:hover {
      background: linear-gradient(135deg, #e6a800 0%, #e6a800 100%) !important;
      transform: translateY(-2px);
      box-shadow: 0 8px 25px -8px rgba(245, 187, 0, 0.4);
    }
  }

  &:not(.active) {
    background: white !important;
    color: #6b7280 !important;
    border: 2px solid #e5e7eb !important;

    &:hover {
      background: #f9fafb !important;
      border-color: #f5bb00 !important;
      color: #f5bb00 !important;
      transform: translateY(-2px);
      box-shadow: 0 8px 25px -8px rgba(245, 187, 0, 0.2);
    }
  }
}

.upload-type-btn>>>.vs-button {
  background: inherit !important;
  color: inherit !important;
  border: inherit !important;

  &:hover {
    background: inherit !important;
    color: inherit !important;
    border: inherit !important;
  }
}

.drop-zone {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -8px rgba(0, 0, 0, 0.2);
  }
}

.progress-bar-inner {
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.file-item {
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8fafc;
  }
}

.status-completed {
  color: #10b981;
}

.status-failed {
  color: #ef4444;
}

.status-completed_with_errors {
  color: #f59e0b;
}

.status-processing {
  color: #3b82f6;
}

.status-pending {
  color: #6b7280;
}

/* Custom scrollbar for modal content */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.status-bg-completed {
  background-color: #d1fae5;
  color: #065f46;
}

.status-bg-failed {
  background-color: #fee2e2;
  color: #991b1b;
}

.status-bg-processing {
  background-color: #dbeafe;
  color: #1e40af;
}

.status-bg-pending {
  background-color: #f3f4f6;
  color: #374151;
}

// GRID BUTTON STYLES
.btn-grid-start {
  background-color: white !important;
  color: #f5bb00 !important;
  border: 2px solid #f5bb00 !important;
}
.btn-grid-start:hover {
  background-color: #f5bb00 !important;
  color: white !important;
}

.btn-grid-completed {
  background-color: white !important; /* Changed from green to white */
  color: #3bb143 !important;          /* Changed from white to green */
  border: 2px solid #3bb143 !important;
}
.btn-grid-completed:hover {
  background-color: #3bb143 !important; /* Changed from white to green */
  color: white !important;              /* Changed from green to white */
  border-color: #3bb143 !important;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate3d(0, 40px, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>